/**
 * Porch / Structural Post Renderer
 *
 * Plan view of a post: its footprint, drawn to actual size, with the diagonal
 * cross that marks a cut structural member on an architectural plan.
 *
 * Each item: { id, x, y, rotation, size, style, material, base, height }
 */

import { GRID_SIZE } from '../../../constants/grid';
import { getPostFootprint, getPostColor } from '../../../constants/posts';

const SELECTION_COLOR = '#00c8ff';
const PX_PER_INCH = GRID_SIZE / 6; // 6" per grid cell, matching the other renderers

// A 6x6 is only 5.5" across - under 19px on screen. Below this it stops reading as
// a shape at all, so the symbol is floored rather than drawn sub-pixel.
const MIN_SYMBOL_PX = 7;

export function drawPost(ctx, post, isSelected = false, scale = 1) {
  const { x, y, rotation = 0 } = post;
  const footprint = getPostFootprint(post);
  const color = getPostColor(post);

  const w = Math.max(MIN_SYMBOL_PX, footprint.width * PX_PER_INCH);
  const d = Math.max(MIN_SYMBOL_PX, footprint.depth * PX_PER_INCH);
  const isRound = footprint.shape === 'round';

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotation * Math.PI) / 180);

  ctx.lineWidth = Math.max(0.75, 1.25 / scale);
  ctx.fillStyle = color;
  ctx.strokeStyle = isSelected ? SELECTION_COLOR : '#2b2b2b';

  // Footprint
  ctx.beginPath();
  if (isRound) {
    ctx.arc(0, 0, w / 2, 0, Math.PI * 2);
  } else {
    ctx.rect(-w / 2, -d / 2, w, d);
  }
  ctx.fill();
  ctx.stroke();

  // Diagonal cross - the plan convention for a post cut through
  ctx.beginPath();
  if (isRound) {
    const r = (w / 2) * 0.707;
    ctx.moveTo(-r, -r); ctx.lineTo(r, r);
    ctx.moveTo(-r, r); ctx.lineTo(r, -r);
  } else {
    ctx.moveTo(-w / 2, -d / 2); ctx.lineTo(w / 2, d / 2);
    ctx.moveTo(-w / 2, d / 2); ctx.lineTo(w / 2, -d / 2);
  }
  ctx.stroke();

  if (isSelected) {
    const pad = Math.max(3, 4 / scale);
    ctx.strokeStyle = SELECTION_COLOR;
    ctx.lineWidth = Math.max(1, 1.5 / scale);
    ctx.setLineDash([4 / scale, 3 / scale]);
    ctx.strokeRect(-w / 2 - pad, -d / 2 - pad, w + pad * 2, d + pad * 2);
    ctx.setLineDash([]);
  }

  ctx.restore();
}

/**
 * Hit test with a forgiving margin - posts are small targets, and on a touch
 * screen an exact-footprint test makes them nearly unselectable.
 */
export function hitTestPost(post, pos, scale = 1) {
  const footprint = getPostFootprint(post);
  const w = Math.max(MIN_SYMBOL_PX, footprint.width * PX_PER_INCH);
  const d = Math.max(MIN_SYMBOL_PX, footprint.depth * PX_PER_INCH);
  const margin = Math.max(4, 6 / scale);

  const dx = pos.x - post.x;
  const dy = pos.y - post.y;
  const rad = -((post.rotation || 0) * Math.PI) / 180;
  const rx = dx * Math.cos(rad) - dy * Math.sin(rad);
  const ry = dx * Math.sin(rad) + dy * Math.cos(rad);

  return Math.abs(rx) <= w / 2 + margin && Math.abs(ry) <= d / 2 + margin;
}
